"use client";

import React, { useCallback, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import {
  type IFilter,
  BookingDateModalMobile,
  BookingGuestModalMobile,
  SearchSectionBox,
  useFilterLabels,
  BookingMessage,
} from "@/src/shared/features/search";
import SubmitButton from "@/src/shared/components/SubmitButton";
import type { IListing } from "@/src/shared/types/listing";
import BookingModalBasicInfo from "@/src/features/listing/components/BookingModalBasicInfo";
import PriceDisplay from "@/src/features/listing/components/PriceDisplay";
import UserAvatar from "@/src/shared/components/atoms/UserAvatar";
import type { UseFormReturn } from "react-hook-form";
import type { BookingFormValues } from "@/src/shared/types/schemaType";
import {
  formatGuestText,
  getFormattedDateRange,
} from "@/src/shared/utils/global";
import { instantBookingModel } from "@/src/shared/features/booking";
import type { BlockedDate } from "@/src/shared/types/blockedDate";
import { useLockBodyScroll } from "@/src/shared/hooks/useLockBodyScroll";

interface IBookingBottomSheetProps {
  open: boolean;
  setFilters: React.Dispatch<React.SetStateAction<IFilter>>;
  filters: IFilter;
  onClose: () => void;
  singleListingData: IListing;
  onSubmit: (e?: React.BaseSyntheticEvent) => Promise<void>;
  isSubmitting: boolean;
  forContactHost?: boolean;
  buttonText?: string;
  method: UseFormReturn<BookingFormValues>;
  handleUpdateQuery: () => void;
  blockedDates?: BlockedDate[];
  checkoutOnlyDates?: string[]; // newly added
}

const BookingBottomSheet: React.FC<IBookingBottomSheetProps> = ({
  open,
  onClose,
  filters,
  setFilters,
  onSubmit,
  isSubmitting,
  singleListingData,
  forContactHost = false,
  buttonText,
  method,
  handleUpdateQuery,
  blockedDates,
  checkoutOnlyDates = [],
}) => {
  const [activeSection, setActiveSection] = useState<
    "main" | "when" | "who" | "message"
  >("main");
  const { dateRangeLabel } = useFilterLabels(filters);

  const isInstantBooking = instantBookingModel(singleListingData);

  const {
    register,
    setValue,
    formState: { errors },
  } = method;

  useLockBodyScroll(open);

  const updateGuestCount = (
    type: keyof IFilter["guests"],
    op: "add" | "subtract",
  ) => {
    setFilters((prevFilters) => {
      const newGuests = { ...prevFilters.guests };
      if (op === "add") {
        newGuests[type] = (newGuests[type] ?? 0) + 1;
      } else {
        newGuests[type] = Math.max(0, (newGuests[type] ?? 0) - 1);
      }
      return { ...prevFilters, guests: newGuests };
    });
  };

  const handleBack = () => {
    setActiveSection("main");
  };

  const getFormattedGuestText = useCallback(() => {
    return formatGuestText(filters.guests);
  }, [filters]);

  const getTotalGuests = useCallback(() => {
    const formattedText = getFormattedGuestText();
    return formattedText || "Select Guests";
  }, [getFormattedGuestText]);

  const renderMain = () => (
    <motion.div
      initial={{ y: "100%" }}
      animate={{ y: 0 }}
      exit={{ y: "100%" }}
      transition={{ type: "spring", damping: 30, stiffness: 300 }}
      className="fixed inset-0 bg-white z-50 p-4"
    >
      {(activeSection === "who" || activeSection === "when") && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.6 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="absolute inset-0 bg-black/50 pointer-events-none z-10"
        />
      )}

      <form
        onSubmit={onSubmit}
        className="scrollbar-hide px-4 space-y-4 overflow-y-auto overscroll-contain h-full"
      >
        <div className="flex items-center justify-between  border-b border-gray-200">
          <button type="button" onClick={onClose} className="p-2">
            <X size={24} />
          </button>
          <h2 className="text-base  md:text-lg font-semibold">
            {forContactHost ? "Contact Host" : "Book Now"}
          </h2>
          <div className="mb-2">
            <PriceDisplay filters={filters} variant="mobile" />
          </div>
        </div>
        {forContactHost ? (
          <>
            {" "}
            <div className="flex items-center gap-3 mb-2">
              <div className="rounded-full overflow-hidden">
                <UserAvatar
                  size="sm"
                  imageUrl={singleListingData.host?.image?.url}
                  firstName={singleListingData.host.first_name ?? ""}
                  lastName={singleListingData.host.last_name ?? ""}
                />
              </div>
              <div>
                <div className="text-brand font-semibold text-lg">
                  Hello ! I am {singleListingData.host.first_name}
                </div>
                <div className="text-ink-gray text-xs">
                  Thank you for choosing our page.
                </div>
              </div>
            </div>
            <BookingModalBasicInfo singleListingData={singleListingData} />
          </>
        ) : null}

        <div className="text-15 font-medium text-ink-black">
          Tell me your preferred date.
        </div>

        <SearchSectionBox
          title="Check In – Check Out"
          value={dateRangeLabel}
          onClick={() => setActiveSection("when")}
          icon={
            <img src="/icons/calendar.png" alt="Calendar" className="w-5 h-5" />
          }
          error={errors?.dates?.message || ""}
        />

        <SearchSectionBox
          title="Guest"
          value={getTotalGuests()}
          onClick={() => setActiveSection("who")}
          icon={
            <img src="/icons/users.png" alt="Profile" className="w-5 h-5" />
          }
          error={errors?.guest?.message || ""}
        />

        {(!isInstantBooking || forContactHost) && (
          <BookingMessage
            aria-invalid={!!errors?.message}
            aria-describedby={errors?.message ? "message-error" : undefined}
            error={errors}
            register={register}
            onClick={() => {
              setActiveSection("message");
            }}
          />
        )}

        <div className="pt-2">
          <SubmitButton
            label={buttonText ? buttonText : "Send Message"}
            isLoading={isSubmitting}
            type="submit"
          />
        </div>
      </form>
    </motion.div>
  );

  return (
    <>
      {open && (
        <div className="md:hidden">
          {renderMain()}
          <AnimatePresence>
            {activeSection === "when" && (
              <BookingDateModalMobile
                appearance="app"
                blockedDates={blockedDates}
                checkoutOnlyDates={checkoutOnlyDates}
                onSave={handleUpdateQuery}
                onBack={handleBack}
                filters={filters}
                setFilters={(update: React.SetStateAction<IFilter>) => {
                  // The date modal passes an updater; resolve it so the form field gets real dates.
                  const next =
                    typeof update === "function" ? update(filters) : update;
                  setFilters(next);
                  setValue(
                    "dates",
                    getFormattedDateRange(next.checkIn, next.checkOut),
                  );
                }}
              />
            )}
            {activeSection === "who" && (
              <BookingGuestModalMobile
                listing={singleListingData}
                onSave={handleUpdateQuery}
                onBack={handleBack}
                filters={filters}
                updateGuestCount={updateGuestCount}
              />
            )}
          </AnimatePresence>
        </div>
      )}
    </>
  );
};

export default BookingBottomSheet;
